import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

const Arrow = ({ dir, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    aria-label={dir < 0 ? "Foto anterior" : "Próxima foto"}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    className={`absolute top-1/2 z-10 -translate-y-1/2 ${dir < 0 ? "left-3" : "right-3"} grid h-11 w-11 place-items-center rounded-full bg-[#f4f0e6]/85 text-[#1a382b] shadow-lg backdrop-blur-md transition-[transform,background-color] duration-300 hover:scale-110 hover:bg-[#7ab51d]`}
  >
    {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

export const Gallery = ({ fotos = [], alt, aspect = "aspect-[4/3]", thumbs = false, testid = "gallery", className = "" }) => {
  const [i, setI] = useState(0);
  const touch = useRef(null);
  const n = fotos.length;
  const go = (d) => setI((p) => (p + d + n) % n);
  if (!n) return null;

  return (
    <div data-testid={testid} className={className}>
      <div
        className={`group relative overflow-hidden bg-[#1a382b] ${aspect}`}
        onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        }}
      >
        {fotos.map((src, k) => (
          <motion.img
            key={src + k}
            src={src}
            alt={`${alt} - foto ${k + 1}`}
            loading={k === 0 ? "eager" : "lazy"}
            draggable={false}
            initial={false}
            animate={{ opacity: k === i ? 1 : 0, scale: k === i ? 1 : 1.07 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full select-none object-cover"
          />
        ))}
        {n > 1 && (
          <>
            <Arrow dir={-1} onClick={() => go(-1)} testid={`${testid}-prev-button`} />
            <Arrow dir={1} onClick={() => go(1)} testid={`${testid}-next-button`} />
          </>
        )}
        <span
          data-testid={`${testid}-counter`}
          className="absolute bottom-3 right-3 z-10 rounded-full bg-[#14291f]/75 px-3 py-1 text-xs tracking-[0.2em] text-[#f3efe4] backdrop-blur"
        >
          {pad(i + 1)} / {pad(n)}
        </span>
      </div>
      {thumbs && n > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-lenis-prevent>
          {fotos.map((src, k) => (
            <button
              key={src + k}
              type="button"
              data-testid={`${testid}-thumb-${k}`}
              onClick={() => setI(k)}
              className={`relative h-16 w-24 shrink-0 overflow-hidden transition-[opacity,outline-color] duration-300 sm:h-20 sm:w-28 ${
                k === i ? "opacity-100 outline outline-2 outline-offset-2 outline-[#7ab51d]" : "opacity-55 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
